import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Mail, ArrowRight, Check, Target, Users, BookOpen, Rocket, Heart, Globe, Briefcase, ArrowLeft, Camera, Image as ImageIcon } from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn } from '../utils';
import { interests } from '../utils/interests';
import { handleImageUpload } from '../utils/imageUtils';
import Footer from '../components/Footer';

const Onboarding = () => {
  const location = useLocation();
  const { user, signup, updateUser } = useAuth();
  const [step, setStep] = useState(user ? 2 : 1); // 1: Welcome/Signup, 2: Interests, 3: Avatar, 4: Success
  const [email, setEmail] = useState(user?.email || '');
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [avatar, setAvatar] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = React.useRef(null);
  const navigate = useNavigate();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const resizedImage = await handleImageUpload(file, { maxSizeMB: 2, maxWidth: 500, maxHeight: 500 });
        setAvatar(resizedImage);
      } catch (err) {
        alert(err.message);
      }
    }
  };

  const handleNext = async () => {
    if (step === 1 && email) {
      if (!user) {
        navigate('/signup', { state: { email, from: location.state?.from } });
      } else {
        setStep(2);
      }
    }
    else if (step === 2) {
      setLoading(true);
      const res = await updateUser({ interests: selectedInterests });
      setLoading(false);
      if (res.success) {
        setStep(3);
      } else {
        // Fallback or error toast could go here
        setStep(3); // Continue anyway for demo/UX if minor failure
      }
    }
    else if (step === 3) {
      if (avatar) {
        setLoading(true);
        await updateUser({ avatar });
        setLoading(false);
      }
      setStep(4);
    }
  };

  const toggleInterest = (id) => {
    if (selectedInterests.includes(id)) {
      setSelectedInterests(selectedInterests.filter(i => i !== id));
    } else {
      setSelectedInterests([...selectedInterests, id]);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <div className={cn(
          "w-full mb-8 transition-all duration-500",
          step === 2 ? "max-w-4xl" : "max-w-md"
        )}>
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-brand-indigo font-bold text-sm transition-all mb-6 group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>
          <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div 
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white p-8 md:p-12 rounded-3xl border border-gray-100 shadow-2xl shadow-indigo-100/30 flex flex-col items-center md:items-start text-center md:text-left"
            >
              <div className="w-16 h-16 bg-brand-indigo rounded-2xl flex items-center justify-center text-white mb-8 shadow-xl shadow-brand-indigo/20">
                <Zap size={32} />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-3">Join Chooziq</h1>
              <p className="text-gray-500 font-medium mb-10">Start participating in live interactions and building persistent community knowledge.</p>

              <div className="space-y-4 w-full">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input 
                    type="email" 
                    placeholder="Enter your email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-bold transition-all outline-none"
                  />
                </div>
                <button 
                  onClick={handleNext}
                  disabled={!email}
                  className="w-full bg-brand-indigo text-white font-bold py-4 rounded-2xl shadow-xl shadow-brand-indigo/10 flex items-center justify-center gap-2 hover:bg-brand-indigo/90 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  Continue to Signup
                  <ArrowRight size={18} />
                </button>
              </div>

              <div className="mt-10 pt-8 border-t border-gray-50 text-center w-full">
                <p className="text-xs text-gray-400 font-bold uppercase tracking-[0.15em] mb-6">Or continue with</p>
                <div className="grid grid-cols-2 gap-4">
                  <button className="flex items-center justify-center gap-2 py-3.5 px-4 rounded-2xl border border-gray-100 font-bold text-sm hover:bg-gray-50 transition-all text-gray-700">
                    Google
                  </button>
                  <button className="flex items-center justify-center gap-2 py-3.5 px-4 rounded-2xl border border-gray-100 font-bold text-sm hover:bg-gray-50 transition-all text-gray-700">
                    GitHub
                  </button>
                </div>
                <p className="mt-8 text-sm text-gray-500 font-bold">
                  Already have an account? 
                  <Link to="/login" state={{ from: location.state?.from }} className="text-brand-indigo hover:underline ml-1">Sign In</Link>
                </p>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div 
              key="step2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white p-8 md:p-12 rounded-3xl border border-gray-100 shadow-2xl shadow-indigo-100/30 flex flex-col items-center md:items-start text-center md:text-left"
            >
              <h2 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">What interests you?</h2>
              <p className="text-gray-500 font-medium mb-10 text-sm">We'll recommend rooms and communities based on your choices.</p>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-10 w-full">
                {interests.map((interest) => (
                  <button
                    key={interest.id}
                    onClick={() => toggleInterest(interest.id)}
                    className={cn(
                      "flex flex-col items-center justify-center p-5 rounded-3xl border-2 transition-all gap-3 active:scale-95",
                      selectedInterests.includes(interest.id) 
                        ? "border-brand-indigo bg-brand-indigo/5 text-brand-indigo shadow-lg shadow-brand-indigo/5" 
                        : "border-gray-50 bg-white text-gray-400 hover:border-gray-100"
                    )}
                  >
                    <div className={cn(
                      "w-12 h-12 rounded-2xl flex items-center justify-center transition-all shadow-sm",
                      selectedInterests.includes(interest.id) ? "bg-brand-indigo text-white shadow-xl shadow-brand-indigo/20" : "bg-gray-50"
                    )}>
                      {interest.icon}
                    </div>
                    <span className="text-xxs font-bold uppercase tracking-[0.15em]">{interest.label}</span>
                  </button>
                ))}
              </div>

              <button 
                onClick={handleNext}
                disabled={selectedInterests.length === 0}
                className="w-full bg-brand-indigo text-white font-bold py-4 rounded-2xl shadow-xl shadow-brand-indigo/10 flex items-center justify-center gap-2 hover:bg-brand-indigo/90 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                Set My Interests
                <ArrowRight size={18} />
              </button>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div 
              key="step3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white p-8 md:p-12 rounded-3xl border border-gray-100 shadow-2xl shadow-indigo-100/30 flex flex-col items-center md:items-start text-center md:text-left"
            >
              <h2 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">Almost there!</h2>
              <p className="text-gray-500 font-medium mb-10 text-sm">Add a profile picture so people recognize you in live rooms.</p>

              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileChange} 
                accept="image/*" 
                className="hidden" 
              />

              <div className="w-full flex justify-center mb-10">
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="w-40 h-40 rounded-3xl bg-gray-50 border-4 border-white shadow-2xl overflow-hidden relative group cursor-pointer active:scale-95"
                >
                  {avatar ? (
                    <img src={avatar} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-300">
                      <Camera size={48} />
                      <span className="text-xxs font-bold uppercase tracking-[0.15em] mt-2">Upload</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <ImageIcon size={24} className="text-white" />
                  </div>
                </div>
              </div>

              <button 
                onClick={handleNext}
                className="w-full bg-brand-indigo text-white font-bold py-4 rounded-2xl shadow-xl shadow-brand-indigo/10 flex items-center justify-center gap-2 hover:bg-brand-indigo/90 transition-all active:scale-[0.98]"
              >
                {avatar ? 'This looks great!' : 'Skip for now'}
                <ArrowRight size={18} />
              </button>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div 
              key="step4"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white p-10 md:p-16 rounded-3xl border border-gray-100 shadow-2xl shadow-indigo-100/30 text-center"
            >
              <div className="w-24 h-24 bg-green-500 rounded-3xl flex items-center justify-center text-white mb-10 mx-auto shadow-2xl shadow-green-200">
                <Check size={48} />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 tracking-tight mb-4">Welcome to Chooziq!</h2>
              <p className="text-gray-500 font-medium mb-12 text-lg">Your account is ready. Start participating in live rooms and communities.</p>

              <button 
                onClick={() => {
                  const from = location.state?.from?.pathname || '/';
                  navigate(from, { replace: true });
                }}
                className="w-full bg-brand-indigo text-white font-bold py-5 rounded-3xl shadow-2xl shadow-brand-indigo/20 flex items-center justify-center gap-3 hover:bg-brand-indigo/90 transition-all active:scale-[0.98]"
              >
                Go to Dashboard
                <ArrowRight size={20} />
              </button>
            </motion.div>
          )}
          </AnimatePresence>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Onboarding;
